import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Loader2, AlertTriangle, Users, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI } from '../../services/api';

export default function DeleteAgentDialog({ open, onOpenChange, agent, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [permanentDelete, setPermanentDelete] = useState(false);

  const hasActiveStudents = (agent?.totalStudents || agent?.assignedStudents?.length || 0) > 0;
  const hasPendingCommissions = (agent?.pendingEarnings || 0) > 0;

  const handleDelete = async () => {
    setLoading(true);

    try {
      const agentId = agent.userId || agent._id;
      await adminAPI.deleteAgent(agentId, permanentDelete);

      toast.success(
        permanentDelete
          ? 'Agent permanently deleted'
          : 'Agent deactivated successfully'
      );

      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete agent';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Delete Agent
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete {agent?.firstName} {agent?.lastName}?
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Agent Info */}
          <div className="p-4 bg-stone-50 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-semibold">
                {agent?.firstName?.[0]}{agent?.lastName?.[0]}
              </div>
              <div>
                <p className="font-medium text-gray-900">
                  {agent?.firstName} {agent?.lastName}
                </p>
                <p className="text-sm text-gray-500">{agent?.email}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-gray-400" />
                <span>{agent?.totalStudents || agent?.assignedStudents?.length || 0} students</span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-gray-400" />
                <span>${agent?.totalEarnings || 0} earned</span>
              </div>
            </div>
          </div>

          {/* Warnings */}
          {(hasActiveStudents || hasPendingCommissions) && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm font-medium text-amber-800 mb-2">
                Warning: This agent has active data
              </p>
              <ul className="text-sm text-amber-700 space-y-1">
                {hasActiveStudents && (
                  <li>
                    - {agent?.totalStudents || agent?.assignedStudents?.length} assigned student(s)
                  </li>
                )}
                {hasPendingCommissions && (
                  <li>- ${agent?.pendingEarnings} in pending commissions</li>
                )}
              </ul>
            </div>
          )}

          {/* Delete options */}
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 border rounded-lg">
              <Checkbox
                id="deactivate"
                checked={!permanentDelete}
                onCheckedChange={() => setPermanentDelete(false)}
              />
              <div className="space-y-1">
                <Label
                  htmlFor="deactivate"
                  className="font-medium cursor-pointer"
                >
                  Deactivate (Recommended)
                </Label>
                <p className="text-sm text-gray-500">
                  The agent will be disabled but data will be preserved. Can be
                  reactivated later.
                </p>
              </div>
            </div>

            <div
              className={`flex items-start gap-3 p-3 border rounded-lg ${
                hasActiveStudents || hasPendingCommissions
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
              }`}
            >
              <Checkbox
                id="permanent"
                checked={permanentDelete}
                onCheckedChange={() => setPermanentDelete(true)}
                disabled={hasActiveStudents || hasPendingCommissions}
              />
              <div className="space-y-1">
                <Label
                  htmlFor="permanent"
                  className={`font-medium ${
                    hasActiveStudents || hasPendingCommissions
                      ? 'cursor-not-allowed'
                      : 'cursor-pointer'
                  }`}
                >
                  Permanently Delete
                </Label>
                <p className="text-sm text-gray-500">
                  Permanently remove the agent and all associated data. This
                  action cannot be undone.
                </p>
                {(hasActiveStudents || hasPendingCommissions) && (
                  <p className="text-xs text-red-500">
                    Cannot permanently delete agents with active students or pending commissions
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : permanentDelete ? (
              'Permanently Delete'
            ) : (
              'Deactivate Agent'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
